import { memo } from "react";
import { Button, EmptyState, Flex, Heading, Icon, Icons, Paragraph, Division } from "@ir/ui";

interface PromotionsEmptyStateProps {
  isLoading: boolean;
  error: Error | null;
  hasPromotions: boolean;
  isRefreshing: boolean;
  isSearchOrFilterApplied: boolean;
  onClearFilters: () => void;
}

const PromotionsEmptyState = memo(
  ({
    isLoading,
    error,
    hasPromotions,
    isRefreshing,
    isSearchOrFilterApplied,
    onClearFilters,
  }: PromotionsEmptyStateProps) => {
    if (isLoading) {
      return null;
    }

    if (error) {
      return (
        <Division className="bg-error-25 border-1 border-color-error-100 px-4 py-3 mt-4 rounded-medium" role="alert">
          <Flex align="center" gap={2}>
            <Icon name={Icons.AlertCircle} size="small" color="error" />
            <Paragraph className="text-error-700 font-medium mb-0">
              Unable to load promotions. Please refresh or try again shortly.
            </Paragraph>
          </Flex>
        </Division>
      );
    }

    if (!hasPromotions && !isRefreshing) {
      if (isSearchOrFilterApplied) {
        return (
          <EmptyState
            className="bg-white border-1 border-color-grey-25 px-4 py-5 mt-4"
            icon={Icons.InfoOctagon}
            title={
              <Heading level={6} className="text-grey-700 font-bold mt-3">
                No promos match your current filters.
              </Heading>
            }
            description={
              <Button variant="outline" color="grey" size="small" onPress={onClearFilters}>
                Clear filters
              </Button>
            }
          />
        );
      }

      return (
        <EmptyState
          className="bg-white border-1 border-color-grey-25 px-4 py-5 mt-4"
          icon={Icons.Calendar}
          title={
            <Heading level={6} className="text-grey-700 font-bold mt-3">
              No trade promotions yet
            </Heading>
          }
          description={
            <Flex direction="column" align="center" gap={2}>
              <Paragraph className="text-grey-500 font-regular mb-0">
                Create your first promotion to start matching deductions automatically.
              </Paragraph>
            </Flex>
          }
        />
      );
    }

    return null;
  }
);

PromotionsEmptyState.displayName = "PromotionsEmptyState";

export { PromotionsEmptyState };
